import { useState } from "react";

interface UsePasswordChangeProps {
  userData: any;
}

export function usePasswordChange({ userData }: UsePasswordChangeProps) {
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordChangeStatus, setPasswordChangeStatus] = useState<
    "success" | "error" | null
  >(null);
  const [passwordChangeError, setPasswordChangeError] = useState<string>("");
  const [emailSentSuccessfully, setEmailSentSuccessfully] = useState(false);

  // Función para solicitar cambio de contraseña en Auth0
  const requestPasswordChange = async (): Promise<void> => {
    try {
      const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN;
      const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;

      const response = await fetch(
        `https://${auth0Domain}/dbconnections/change_password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            client_id: clientId,
            email: userData.auth0Profile?.email,
            connection: "Username-Password-Authentication",
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.text();
        console.error("Auth0 API Error Response:", errorData);

        let parsedError;
        try {
          parsedError = JSON.parse(errorData);
        } catch {
          parsedError = { message: errorData };
        }

        // Manejar errores específicos de Auth0
        if (response.status === 400) {
          if (
            parsedError.error === "invalid_user" ||
            parsedError.message?.includes("user")
          ) {
            throw new Error("Usuario no encontrado o no válido");
          } else if (
            parsedError.error === "invalid_connection" ||
            parsedError.message?.includes("connection")
          ) {
            throw new Error("Configuración de conexión no válida");
          } else if (parsedError.message?.includes("password")) {
            throw new Error("Error relacionado con la contraseña");
          } else {
            throw new Error(
              parsedError.message || "Error de validación en la solicitud",
            );
          }
        }

        throw new Error(
          parsedError.message ||
            `Error ${response.status}: No se pudo procesar la solicitud`,
        );
      }
    } catch (error) {
      console.error("Error requesting password change in Auth0:", error);
      throw error;
    }
  };

  const handlePasswordChange = async () => {
    setPasswordChangeError("");
    setIsChangingPassword(true);

    try {
      // Solicitar cambio de contraseña
      // Auth0 enviará un email al usuario con un enlace para cambiar la contraseña
      await requestPasswordChange();

      // Marcar que el email fue enviado exitosamente
      setEmailSentSuccessfully(true);

      // Mostrar mensaje de éxito
      setPasswordChangeStatus("success");
    } catch (error) {
      console.error("Error requesting password change:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Error desconocido al solicitar el cambio de contraseña";
      setPasswordChangeError(errorMessage);
      setPasswordChangeStatus("error");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleClosePasswordModal = () => {
    setPasswordChangeStatus(null);
    setPasswordChangeError("");
  };

  return {
    // States
    isChangingPassword,
    passwordChangeStatus,
    passwordChangeError,
    emailSentSuccessfully,

    // Handlers
    handlePasswordChange,
    handleClosePasswordModal,
  };
}
